import { ColorProp } from "styles/themes";

import { FeatureEntityType } from "lib/types/graphQLEnums";
import { pluralise } from "lib/utils";

/** Days used as the chart and legend print them, e.g. "1 day", "6 days". */
export function formatDaysUsed(daysUsed: number): string {
  return pluralise(daysUsed, "day", true);
}

/** A share of the days used, 0 to 1, as a whole percentage, e.g. "25%". */
export function formatShare(share: number): string {
  return `${Math.round(share * 100)}%`;
}

type FeatureConfig = {
  label: string;
  color: ColorProp;
  tooltipFormatter: (daysUsed: number) => string;
};

function feature(label: string, color: ColorProp): FeatureConfig {
  return { label, color, tooltipFormatter: formatDaysUsed };
}

/**
 * Each feature's name, and its colour in the Feature usage pie, legend and bars. Also the pie's
 * `ChartConfig`, keyed on the entity type each slice names. Notices split by stage take three
 * shades of one blue, so they read as one family and the features fit the palette's ten colours.
 */
export const FEATURE_USAGE_CONFIG: Record<FeatureEntityType, FeatureConfig> = {
  [FeatureEntityType.Notice]: feature("Notices", "sysChart1"),
  [FeatureEntityType.PreTender]: feature("Pre-tenders", "blue300"),
  [FeatureEntityType.Tender]: feature("Tenders", "sysChart1"),
  [FeatureEntityType.Award]: feature("Awards", "blue700"),
  [FeatureEntityType.Buyer]: feature("Buyers", "sysChart2"),
  [FeatureEntityType.Supplier]: feature("Suppliers", "sysChart3"),
  [FeatureEntityType.Framework]: feature("Frameworks", "sysChart4"),
  [FeatureEntityType.BuyerDocument]: feature("Buyer documents", "sysChart5"),
  [FeatureEntityType.CustomerDocument]: feature("Content library", "sysChart6"),
  [FeatureEntityType.Contact]: feature("Contacts", "sysChart7"),
  [FeatureEntityType.Spend]: feature("Spend", "sysChart8"),
  [FeatureEntityType.Lead]: feature("Leads", "sysChart9"),
  [FeatureEntityType.Bid]: feature("Bids", "sysChart10"),
};

/** The features notice use splits into by the notice's stage, in place of Notices. */
export const NOTICE_STAGE_FEATURES: readonly FeatureEntityType[] = [
  FeatureEntityType.PreTender,
  FeatureEntityType.Tender,
  FeatureEntityType.Award,
];

/** Whether the Usage page lists a feature: Notices, or with notices split by stage, each stage. */
export function isFeatureShown(
  entityType: FeatureEntityType,
  splitNoticesByStage: boolean,
): boolean {
  if (splitNoticesByStage) return entityType !== FeatureEntityType.Notice;
  return !NOTICE_STAGE_FEATURES.includes(entityType);
}
